import { type ChangeEvent, type KeyboardEvent, type MouseEvent, memo, type Ref, useCallback } from 'react';

import { SearchIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

export type WorkspaceAgentSelectAssetSearchInputProps = {
  activeOptionId: string | undefined;
  inputRef?: Ref<HTMLInputElement>;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  query: string;
};

export const WorkspaceAgentSelectAssetSearchInput = memo(
  ({
    activeOptionId,
    inputRef,
    onChange,
    onClear,
    onKeyDown,
    placeholder,
    query,
  }: WorkspaceAgentSelectAssetSearchInputProps) => {
    const handleClearMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    }, []);

    return (
      <label className="flex h-8 shrink-0 items-center gap-2 rounded-8 border border-gray-5 bg-gray-1 px-2.5 text-gray-10 focus-within:border-altitude-9 focus-within:ring-1 focus-within:ring-altitude-9">
        <SearchIcon aria-hidden className="size-3.5 shrink-0" />
        <span className="sr-only">{placeholder}</span>
        <input
          aria-activedescendant={activeOptionId}
          className="min-w-0 flex-1 bg-transparent text-12 text-gray-12 leading-4 outline-none placeholder:text-gray-9"
          data-testid="WORKSPACE_AGENT_SELECT_ASSET_SEARCH"
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          value={query}
        />
        {query.length > 0 ? (
          <IconButton
            appearance="ghost"
            color="gray"
            data-testid="WORKSPACE_AGENT_SELECT_ASSET_SEARCH_CLEAR"
            icon={XIcon}
            label="Clear search"
            onClick={onClear}
            onMouseDown={handleClearMouseDown}
            size="extra-small"
          />
        ) : null}
      </label>
    );
  },
);

WorkspaceAgentSelectAssetSearchInput.displayName = 'WorkspaceAgentSelectAssetSearchInput';
